import { ViewConfig } from "@lib/views";
import {
    AMOUNT_COLUMN_SIZE,
    CATEGORY_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { Category } from "@common/types/category";
import { Entity } from "@common/types/entity";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { HistoryDetailsEntityCell } from "../../cells/HistoryDetailsEntityCell";
import { HistoryDetailsAmountCell } from "../../cells/HistoryDetailsAmountCell";
import { SplitCategoryCell } from "../cells/SplitCategoryCell";
import { SplitLedgerCell } from "../cells/SplitLedgerCell";
import { SplitDetailDateCell } from "../cells/SplitDetailDateCell";
import { SplitDescriptionCell } from "../cells/SplitDescriptionCell";
import { SplitFinancialAccountCell } from "../cells/SplitFinancialAccountCell";
import { SplitLedgerInfo } from "../schema";

export interface SplitDetailRow extends HistoryDetailsBaseRow {
    id: string;
    bankDescription: string;
    category: Partial<Category>;
    ledger: SplitLedgerInfo;
    amount: number;
    originalAmount: number;
    entity: Partial<Entity>;
    financialAccountId: number | null;
}

export enum SplitDetailColumn {
    DATE = "date",
    DESCRIPTION = "bankDescription",
    CATEGORY = "category",
    LEDGER = "ledger",
    AMOUNT = "amount",
    ACCOUNT = "financialAccount",
    ENTITY = "entity",
}

type SplitDetailsViewConfig = ViewConfig<SplitDetailRow, string, never>;

const CATEGORY_CLASSIFICATION_COLUMN: SplitDetailsViewConfig["columns"] = {
    [SplitDetailColumn.CATEGORY]: {
        accessorKey: "category",
        name: "Category",
        header: "Category",
        cell: SplitCategoryCell,
        ...CATEGORY_COLUMN_SIZE,
    },
};

const LEDGER_CLASSIFICATION_COLUMN: SplitDetailsViewConfig["columns"] = {
    [SplitDetailColumn.LEDGER]: {
        accessorKey: "ledger",
        name: "Cash ledger",
        header: "Cash ledger",
        cell: SplitLedgerCell,
        ...CATEGORY_COLUMN_SIZE,
    },
};

export const createSplitDetailsViewConfig = (
    isGlFirst: boolean,
): SplitDetailsViewConfig => ({
    columns: {
        [SplitDetailColumn.DATE]: {
            accessorKey: "date",
            name: "Date",
            header: "Date",
            cell: SplitDetailDateCell,
            immutable: true,
            minSize: 120,
            maxSize: 120,
        },
        [SplitDetailColumn.DESCRIPTION]: {
            accessorKey: "bankDescription",
            name: "Description",
            header: "Description",
            cell: SplitDescriptionCell,
            minSize: 200,
            maxSize: 600,
        },
        ...(isGlFirst
            ? LEDGER_CLASSIFICATION_COLUMN
            : CATEGORY_CLASSIFICATION_COLUMN),
        [SplitDetailColumn.AMOUNT]: {
            accessorKey: "amount",
            name: "Amount",
            header: "Amount",
            cell: HistoryDetailsAmountCell,
            ...AMOUNT_COLUMN_SIZE,
        },
        [SplitDetailColumn.ACCOUNT]: {
            accessorKey: "financialAccountId",
            name: "Account",
            header: "Account",
            cell: SplitFinancialAccountCell,
            minSize: 200,
            maxSize: 400,
        },
        [SplitDetailColumn.ENTITY]: {
            accessorKey: "entity",
            name: "Entity",
            header: "Entity",
            cell: HistoryDetailsEntityCell,
            minSize: 56,
            maxSize: 150,
            immutable: true,
        },
    },
});
